import { useCallback, useEffect, useMemo, useState } from "react"
import Icon from "../../components/Icon"
import api from "../../utils/api"
import { formatMoney } from "../../utils/format"
import { getApiErrorMessage } from "../../utils/errorMessage"
import AutoBillModal from "./AutoBillModal"

// Self-contained per-property auto-invoicing manager. Drops into the Rentals
// property modal and the UT-Bills billing console alike. Loads the property's
// own categories + tenant roster, lets the manager set up / edit / turn off
// monthly auto-billing, and toggle individual occupied units on or off.
//
// providerRef is the UT-Bills provider id (for FRP companies this is the bridge
// ref `mc:<companyId>`). property is { _id, name }.
function PropertyAutoInvoicing({ providerRef, property }) {
  const propertyId = property?._id
  const base = `/ut-bills/providers/${encodeURIComponent(providerRef)}`

  const [categories, setCategories] = useState([])
  const [customers, setCustomers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  const [showSetup, setShowSetup] = useState(false)
  const [customerSearch, setCustomerSearch] = useState("")
  const [customersLimit, setCustomersLimit] = useState(5)
  const [unitSavingKey, setUnitSavingKey] = useState(null)

  const load = useCallback(async () => {
    if (!providerRef || !propertyId) return
    setError("")
    try {
      const [catRes, custRes] = await Promise.all([
        api.get(`${base}/categories?property=${encodeURIComponent(propertyId)}`),
        api.get(`${base}/customers?property=${encodeURIComponent(propertyId)}`),
      ])
      const propertyCategories = Array.isArray(catRes?.data?.categories)
        ? catRes.data.categories.filter((category) => String(category?.property?._id || category?.property || "") === String(propertyId))
        : []
      setCategories(propertyCategories)
      setCustomers(Array.isArray(custRes?.data?.customers) ? custRes.data.customers : [])
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to load auto-invoicing."))
    }
  }, [base, providerRef, propertyId])

  useEffect(() => {
    let active = true
    setLoading(true)
    load().finally(() => {
      if (active) setLoading(false)
    })
    return () => {
      active = false
    }
  }, [load])

  // Callbacks handed to AutoBillModal — property scope is injected here.
  const createCategory = useCallback(
    async (_providerId, payload) => {
      const res = await api.post(`${base}/categories`, { ...payload, property: propertyId })
      return res?.data?.category || null
    },
    [base, propertyId]
  )

  const configure = useCallback(
    async (_providerId, categoryId, payload) => {
      const res = await api.patch(
        `${base}/categories/${encodeURIComponent(categoryId)}/auto-invoice`,
        payload
      )
      if (payload?.enabled === false) {
        await load()
      } else {
        load().catch(() => {})
      }
      return res?.data || null
    },
    [base, load]
  )

  const toggleUnit = useCallback(
    async (customer) => {
      if (unitSavingKey) return
      const next = !customer.autoInvoiceEnabled
      const rowKey = `${customer.customerId}:${customer.unitId}`
      setUnitSavingKey(rowKey)
      setError("")
      setCustomers((prev) =>
        prev.map((c) => (
          c.customerId === customer.customerId && c.unitId === customer.unitId
            ? { ...c, autoInvoiceEnabled: next }
            : c
        ))
      )
      try {
        await api.patch(`${base}/customers/${encodeURIComponent(customer.customerId)}`, {
          autoInvoiceEnabled: next,
          property: propertyId,
          unitId: customer.unitId,
        })
      } catch (err) {
        setCustomers((prev) =>
          prev.map((c) => (
            c.customerId === customer.customerId && c.unitId === customer.unitId
              ? { ...c, autoInvoiceEnabled: !next }
              : c
          ))
        )
        setError(getApiErrorMessage(err, "Failed to update unit."))
      } finally {
        setUnitSavingKey(null)
      }
    },
    [base, propertyId, unitSavingKey]
  )

  const autoBillingActive = useMemo(() => categories.some((c) => c.autoInvoiceEnabled), [categories])
  const activeCount = categories.filter((c) => c.autoInvoiceEnabled).length

  const query = customerSearch.trim().toLowerCase()
  const filteredCustomers = query
    ? customers.filter((c) =>
        [c.name, c.phone, c.idNumber, c.unitLabel].some((f) => String(f || "").toLowerCase().includes(query))
      )
    : customers
  const visibleCustomers = filteredCustomers.slice(0, customersLimit)
  const hiddenCount = filteredCustomers.length - customersLimit
  const onCount = customers.filter((c) => c.autoInvoiceEnabled).length

  if (!propertyId) return null

  return (
    <div className="space-y-4">
      {/* Heading */}
      <div className="flex items-start gap-3 rounded-2xl border border-teal-100 bg-gradient-to-r from-teal-50 to-emerald-50 p-3.5">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-teal-600 text-white shadow-sm">
          <Icon name="arrow-repeat" size="1.05rem" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-black text-slate-900">Monthly auto-invoicing</p>
          <p className="mt-0.5 text-xs text-slate-500">
            {autoBillingActive
              ? `${activeCount} categor${activeCount === 1 ? "y" : "ies"} billing enabled units of ${property.name} automatically.`
              : `Set up recurring charges (e.g. Trash) for occupied units of ${property.name}.`}
          </p>
        </div>
      </div>

      {error && (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700">{error}</p>
      )}

      {/* Active categories */}
      {loading ? (
        <div className="space-y-2">
          {[0, 1].map((i) => (
            <div key={i} className="h-12 animate-pulse rounded-xl bg-slate-100" />
          ))}
        </div>
      ) : (
        <>
          {categories.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {categories.map((category) => {
                const auto = Boolean(category.autoInvoiceEnabled)
                return (
                  <span
                    key={category._id}
                    className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-[11px] font-bold ${
                      auto
                        ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                        : "border-slate-200 bg-slate-50 text-slate-600"
                    }`}
                  >
                    <Icon name={category.kind === "trash" ? "trash3-fill" : "tag"} size="0.6rem" />
                    {category.name}
                    {auto && (
                      <>
                        <Icon name="arrow-repeat" size="0.6rem" className="text-emerald-600" />
                        <span className="rounded bg-white/70 px-1 text-[10px]">
                          {formatMoney(category.autoInvoiceAmount).replace("KES ", "")}
                        </span>
                      </>
                    )}
                  </span>
                )
              })}
            </div>
          )}

          <button
            type="button"
            onClick={() => setShowSetup(true)}
            className={`inline-flex w-full items-center justify-center gap-1.5 rounded-xl border py-2.5 text-xs font-bold transition-colors ${
              autoBillingActive
                ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                : "border-teal-400 bg-teal-600 text-white hover:bg-teal-700"
            }`}
          >
            <Icon name="arrow-repeat" size="0.8rem" />
            {autoBillingActive ? "Manage auto-billing" : "Set up auto invoicing"}
          </button>
        </>
      )}

      {/* Tenant roster */}
      {!loading && (
        <div>
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
              Tenant units ({customers.length})
            </p>
            {customers.length > 0 && (
              <span className="text-[11px] font-semibold text-emerald-600">{onCount} on</span>
            )}
          </div>

          {customers.length > 0 && (
            <div className="relative mb-2">
              <Icon name="search" size="0.75rem" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={customerSearch}
                onChange={(e) => {
                  setCustomerSearch(e.target.value)
                  setCustomersLimit(5)
                }}
                placeholder="Search tenant, phone or unit..."
                className="w-full rounded-xl border border-slate-200 py-2 pl-8 pr-3 text-xs focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100"
              />
            </div>
          )}

          {filteredCustomers.length === 0 ? (
            <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-xs text-slate-500">
              {customerSearch
                ? "No tenant units match your search."
                : "No occupied units in this property yet. They appear here automatically once a tenant is assigned."}
            </p>
          ) : (
            <div className="space-y-1">
              {visibleCustomers.map((customer) => (
                <div
                  key={`${customer.customerId}:${customer.unitId}`}
                  className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/50 px-3 py-2.5"
                >
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-200 text-slate-500">
                    <Icon name="person-fill" size="0.75rem" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-bold text-slate-800">{customer.name || "Tenant"}</p>
                    <p className="truncate text-[11px] text-slate-500">
                      Unit {customer.unitLabel || "—"}
                      {(customer.phone || customer.idNumber) ? ` · ${customer.phone || customer.idNumber}` : ""}
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    onClick={() => toggleUnit(customer)}
                    disabled={unitSavingKey === `${customer.customerId}:${customer.unitId}`}
                    aria-checked={customer.autoInvoiceEnabled}
                    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${
                      customer.autoInvoiceEnabled ? "bg-emerald-500" : "bg-slate-300"
                    }`}
                    title={customer.autoInvoiceEnabled ? "Auto-invoice on for this unit" : "Auto-invoice off for this unit"}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                        customer.autoInvoiceEnabled ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
              ))}

              {hiddenCount > 0 && (
                <button
                  type="button"
                  onClick={() => setCustomersLimit((l) => l + 5)}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white py-2 text-xs font-bold text-slate-600 transition-colors hover:bg-slate-50"
                >
                  Show 5 more ({hiddenCount} remaining)
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {showSetup && (
        <AutoBillModal
          providerId={providerRef}
          providerName={property.name}
          categories={categories}
          onCreateCategory={createCategory}
          onConfigure={configure}
          onClose={() => {
            setShowSetup(false)
            load()
          }}
        />
      )}
    </div>
  )
}

export default PropertyAutoInvoicing
